import { ValueParserFunc } from 'ag-grid-community'
import dayjs from 'dayjs'
import { RadioOption } from '../../components/RadioButtonGroup/RadioButtonGroup'
import { defaultGridOptions } from '../../helpers/formatting'
import {
  ListTradingOrder,
  ListTradingSecurity
} from '../../store/listTrading/types'
import { Order, OrderStatus } from '../../store/order/types'
import { getTypedArray } from '../../store/securities/helpers'

export const isGoodTrade = (
  myAmount: number,
  theirAmount: number | undefined,
  isBid: boolean,
  isSpread: boolean
) => {
  if (typeof theirAmount !== 'number') return false
  const bidMultiplier = isBid ? 1 : -1
  const spreadMultiplier = isSpread ? -1 : 1
  return (myAmount - theirAmount) * bidMultiplier * spreadMultiplier >= 0
}

/*
   Sort comparator, best quote first for the side being traded (same direction
   as isGoodTrade): buying wants the lowest price / highest spread, selling the
   reverse. Orders missing the compared amount sort last, in their given order.
*/
export const compareBestFirst =
  (isBid: boolean, isSpread: boolean) => (a: Order, b: Order) => {
    const amountA = isSpread ? a.spread : a.price
    const amountB = isSpread ? b.spread : b.price
    if (typeof amountA !== 'number') {
      return typeof amountB === 'number' ? 1 : 0
    }
    if (typeof amountB !== 'number') {
      return -1
    }
    const direction = (isBid ? 1 : -1) * (isSpread ? -1 : 1)
    return (amountA - amountB) * direction
  }

export const isEmpty = (value: any): value is null | undefined =>
  value === undefined || value === null

export const isTradeable = (security: ListTradingSecurity) => {
  if (!security.remainingInterest || security.remainingInterest <= 0) {
    return false
  }
  const bestTrade = security.bestOrder
  if (!bestTrade || !bestTrade.canAggress) return false
  const isSpread = !!security.targetSpread
  const myAmount = security.targetSpread ?? security.targetPrice
  const price = Number(bestTrade.displayPrice)
  const theirAmount = isSpread ? bestTrade.spread : price
  if (isEmpty(security.isBid)) return false
  if (isEmpty(myAmount) && (price || bestTrade.spread)) {
    return true
  }
  if (isEmpty(myAmount)) return false

  return isGoodTrade(
    myAmount,
    theirAmount,
    security.isBid,
    !!security.targetSpread
  )
}
export const isPending = ({ status }: Order) =>
  ['pending', 'waitingForConfirmation'].includes(status)
export const getDisplayStatus = (status?: OrderStatus) => {
  if (!status) return ''
  if (status === 'accepted') return 'Traded'
  if (status === 'waitingForConfirmation') return 'Pending'
  const capitalLetters = /[A-Z]/g
  const spaced = status.replace(capitalLetters, (replaced) => ' ' + replaced)
  if (spaced.length < 2) return spaced
  const [initial, ...rest] = spaced
  return initial.toUpperCase() + rest.join('')
}

export const convertToLocalTz = (date?: Date) => {
  if (!date) return ''
  return dayjs(date).format('h:mmA')
}

// create grid only
export type CreateGridType = Partial<ListTradingOrder> & { id: number }
export type TemplateOption = 'Standard' | 'Compressed'
export const createEmptyRow = (id: number) => ({ id })
export const createEmptyRows = (count: number, startId: number = 0) => {
  const blankArray = new Array(count).fill('')
  return blankArray.map((_emptyString, i) => createEmptyRow(i + startId))
}
export const isBidParser: ValueParserFunc<
  CreateGridType,
  CreateGridType['isBid']
> = ({ newValue }) => {
  const lcValue = newValue.toLowerCase()
  return ['b', 'buy', 'offer'].includes(lcValue)
}

export const templateOptions = [
  {
    label: 'Buy/Sell',
    value: 'Standard'
  },
  {
    label: 'Long/Short (+/-)',
    value: 'Compressed'
  }
] satisfies Array<RadioOption<TemplateOption>>

export const isSpreadOptions = [
  {
    label: 'Spread',
    value: true
  },
  {
    label: 'Price',
    value: false
  }
] satisfies Array<RadioOption<boolean | undefined>>

export const convertSpreadsToPrices = (rows: CreateGridType[]) => {
  return rows.map((row) => {
    const spread = undefined
    const price = row.spread
    return { ...row, price, spread }
  })
}
export const convertPricesToSpreads = (rows: CreateGridType[]) => {
  return rows.map((row) => {
    const spread = row.price
    const price = undefined
    return { ...row, price, spread }
  })
}

export const gridOptions = {
  ...defaultGridOptions,
  defaultColDef: {
    suppressHeaderMenuButton: true,
    sortable: false,
    resizable: false,
    suppressMovable: true,
    cellStyle: {
      border: 'var(--border)',
      borderWidth: '0 1px',
      paddingLeft: '0.5rem'
    }
  }
}

export const calculateDiff = (
  security: ListTradingSecurity
): number | undefined => {
  const isSpread = !!security.targetSpread
  const limit = security.targetSpread ?? security.targetPrice
  if (isEmpty(limit)) return undefined
  const best = isSpread
    ? security.bestOrder?.spread
    : security.bestOrder?.displayPrice
    ? Number(security.bestOrder.displayPrice)
    : undefined
  if (isEmpty(best) || Number.isNaN(best)) return undefined
  const diff = best - limit
  if (diff === 0) return undefined
  const bidMultiplier = security.isBid ? 1 : -1
  const spreadMultiplier = isSpread ? 1 : -1
  return bidMultiplier * spreadMultiplier * diff
}

export const formatDiff = (diff: number | undefined): string => {
  if (diff === undefined) return ''
  const decimals = diff.toString().split('.')[1]
  if (!decimals || decimals.length <= 4) return diff.toString()
  return parseFloat(diff.toFixed(4)).toString()
}

export type CreateConfirmGridRow = Pick<
  CreateGridType,
  'cusipOrIsin' | 'isBid' | 'size'
> & { formattedAmt: string }

// paste filtering
type FormatChecker = (data: string[]) => boolean

export type FilterPasteResult =
  | { action: 'paste'; passingRows: string[][]; skippedDataRows: number }
  | {
      action: 'tryAlternate'
      alternateRows: string[][]
      skippedDataRows: number
    }
  | { action: 'reject' }

type RowKind = 'primary' | 'alternate' | 'neither'

const classifyRow = (
  row: string[],
  isRowWrongFormat: FormatChecker,
  alternateIsRowWrongFormat: FormatChecker
): RowKind => {
  if (!isRowWrongFormat(row)) return 'primary'
  if (!alternateIsRowWrongFormat(row)) return 'alternate'
  return 'neither'
}

export const categorizePastedRows = (
  rawRows: string[][],
  isRowWrongFormat: FormatChecker,
  alternateIsRowWrongFormat: FormatChecker
): FilterPasteResult => {
  if (rawRows.length === 0) return { action: 'reject' }

  const primaryRows = getTypedArray<string[]>()
  const alternateRows = getTypedArray<string[]>()
  const neitherRows = getTypedArray<string[]>()
  for (let i = 0; i < rawRows.length; i++) {
    const kind = classifyRow(
      rawRows[i],
      isRowWrongFormat,
      alternateIsRowWrongFormat
    )
    if (kind === 'primary') {
      primaryRows.push(rawRows[i])
    } else if (kind === 'alternate') {
      alternateRows.push(rawRows[i])
    } else if (i === 0 && rawRows.length > 1) {
      /* header — excluded from all buckets */
    } else {
      neitherRows.push(rawRows[i])
    }
  }

  if (primaryRows.length > 0) {
    return {
      action: 'paste',
      passingRows: primaryRows,
      skippedDataRows: alternateRows.length + neitherRows.length
    }
  }

  if (alternateRows.length > 0) {
    return {
      action: 'tryAlternate',
      alternateRows,
      skippedDataRows: neitherRows.length
    }
  }

  return { action: 'reject' }
}
export const standardIsRowWrongFormat = (data: string[]) => {
  const bs = (data[1] || '').trim()
  if (bs === '') return false
  const normalized = bs.toLowerCase()
  if (!(normalized.startsWith('b') || normalized.startsWith('s'))) return true
  return /[^a-z]/i.test(bs)
}
export const compressedIsRowWrongFormat = (data: string[]) => {
  const bs = data[1] || ''
  if (bs === '') return false
  return isNaN(parseInt(bs, 10))
}
